import { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth, userCan } from './AuthContext'
import { useToast } from '@/components/layout/Toast'

/**
 * Route guard for the non-Team Settings pages (Account/Services/Payments/
 * Preferences). A member without the `canAdjustSettings` capability can't reach
 * them — the sidebar already hides the links; this stops direct-URL access too,
 * matching the "hidden, not read-only" decision (#186). The server still
 * enforces the writes regardless. Owners/legacy sessions pass.
 *
 * **Branding is the carve-out** (#509): `/settings/branding` is viewable
 * by every member and editable only with `adjust_settings`, so it is NOT wrapped
 * here and its sidebar entry is NOT `adjustOnly`. #186's blanket "hidden, not
 * read-only" rule holds everywhere else; on that one page the read/write split
 * is the rule, with the server-side write guards (`POST /accounts/:id/logo`,
 * `PATCH /accounts/:id/invoice-template`) as the authority and the disabled
 * controls as presentation only.
 *
 * Blocked members land on /settings/security (the one settings page every
 * signed-in user can use) with a one-line explanation — never on a dead end.
 */
export function RequireAdjustSettings({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const { addToast } = useToast()
  const blocked = !userCan(user, 'canAdjustSettings')
  useEffect(() => {
    if (blocked) addToast('info', 'Settings are managed by account owners', 'settings-gate')
  }, [blocked, addToast])
  if (blocked) {
    return <Navigate to="/settings/security" replace />
  }
  return <>{children}</>
}
